import React, { useEffect, useState } from 'react';

import Avatar from '../../components/Avatar.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { ChatAPI } from '../../lib/api';
import { chatUserInitials, chatUserName } from '../../lib/chatUser';

const PROFILE_FIELDS = [
  ['Email', (u) => u.email],
  ['Username', (u) => u.username],
  ['Phone', (u) => u.phone_number],
  ['Role', (u) => u.role_name],
  ['Division', (u) => u.division_name],
  ['Organisation', (u) => u.organisation_name],
  ['City', (u) => u.city],
  ['State', (u) => u.state],
  ['Address', (u) => u.address],
];

/**
 * View-only identity card opened from a chat name.
 *
 * Seeds from the mini payload already on the message / conversation, then
 * loads ``GET /api/chat/contacts/<id>/`` for the rest of the public
 * profile.  There are no edit controls — that is ``/me``.
 */
export default function ChatUserProfileModal({ user, onClose }) {
  const userId = user && user.id;
  const [profile, setProfile] = useState(user);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setProfile(user);
    setLoading(true);
    setError(null);
    ChatAPI.contact(userId)
      .then((data) => {
        if (!cancelled) setProfile({ ...user, ...data });
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!user) return null;

  const name = chatUserName(profile) || 'User';
  const rows = PROFILE_FIELDS
    .map(([label, read]) => [label, (read(profile || {}) || '').toString().trim()])
    .filter(([, value]) => value);

  return (
    <div
      className="ty-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="ty-modal ty-chat-profile"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ty-chat-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ty-modal__head">
          <h2 id="ty-chat-profile-title" className="ty-modal__title">
            Profile
          </h2>
          <button
            type="button"
            className="ty-modal__close"
            onClick={onClose}
            aria-label="Close profile"
          >
            ×
          </button>
        </header>
        <div className="ty-modal__body">
          <div className="ty-chat-profile__hero">
            <Avatar
              initials={chatUserInitials(profile)}
              imageUrl={profile.profile_image_url || ''}
              name={name}
              size={72}
            />
            <div>
              <div className="ty-chat-profile__name">{name}</div>
              {profile.email ? (
                <div className="ty-chat-profile__email">{profile.email}</div>
              ) : null}
            </div>
          </div>
          {loading ? <Loader label="Loading profile…" /> : null}
          <ErrorBox error={error} />
          {!loading && (
            rows.length ? (
              <dl className="ty-chat-profile__dl">
                {rows.map(([label, value]) => (
                  <div key={label} className="ty-chat-profile__row">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="ty-muted">No extra profile details are available.</p>
            )
          )}
        </div>
        <footer className="ty-modal__foot">
          <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
